import { useTranslations, type TFunction } from '@aikoa/i18n/react'
import { View } from 'react-native'
import Icon, { type MaterialCommunityIconName } from '~/components/Icon'
import { Heading3 } from '~/components/typography/Titles'
import { AnimatedPressable } from '~/components/ui/pressable'
import { cn } from '~/lib/utils'

export const matchScoreToLevel = (score: number | null, t: TFunction<'leads'>) => {
  if (score === null) return t(($) => $.matchLevels.noData)

  if (score >= 4) return t(($) => $.matchLevels.excellent)
  if (score >= 3) return t(($) => $.matchLevels.good)
  if (score >= 2) return t(($) => $.matchLevels.moderate)
  if (score >= 1) return t(($) => $.matchLevels.poor)
  return t(($) => $.matchLevels.notSuitable)
}

const adjustHslPart = (
  hslColor: string,
  part: 'lightness' | 'saturation',
  adjustment: number
): string => {
  const hslMatch = hslColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)

  if (!hslMatch) {
    return hslColor
  }

  const hue = Number.parseInt(hslMatch[1], 10)
  const saturation = Number.parseInt(hslMatch[2], 10)
  const lightness = Number.parseInt(hslMatch[3], 10)

  if (part === 'lightness') {
    return `hsl(${hue}, ${saturation}%, ${Math.min(100, Math.max(0, lightness + adjustment))}%)`
  }

  return `hsl(${hue}, ${Math.min(100, Math.max(0, saturation + adjustment))}%, ${lightness}%)`
}

export const matchScoreToColor = (
  score: number | null,
  variant: 'default' | 'inverted' = 'default'
) => {
  let color = 'hsl(0, 0%, 50%)'
  if (score === null) return 'hsl(0, 0%, 60%)'

  if (score < 1) color = 'hsl(12, 50%, 72%)'
  if (score >= 1) color = 'hsl(20, 70%, 70%)'
  if (score >= 2) color = 'hsl(60, 60%, 60%)'
  if (score >= 3) color = 'hsl(80, 55%, 60%)'
  if (score >= 4) color = 'hsl(120, 60%, 60%)'

  if (variant === 'inverted') {
    return adjustHslPart(adjustHslPart(color, 'lightness', -25), 'saturation', 10)
  }

  return color
}

export const matchScoreToTextColor = (score: number | null) => {
  const backgroundColor = matchScoreToColor(score, 'default')
  return adjustHslPart(backgroundColor, 'lightness', -60)
}

// Normalize the score to match hexagon fill pattern
const hexagonScore = [0, 1, 3, 5, 6]

export const getHexagonIconName = (
  score: number | null
): MaterialCommunityIconName => {
  // eslint-disable-next-line i18next/no-literal-string
  if (score === null) return 'cancel'
  if (!score) return 'hexagon-outline'
  return `hexagon-slice-${hexagonScore[score]}` as MaterialCommunityIconName
}

export function MatchBadge({
  score,
  variant = 'default',
  onPress,
  onPointerEnter,
  onPointerLeave,
  className,
  label,
}: {
  score: number | null
  variant?: 'default' | 'inverted'
  onPress?: () => void
  onPointerEnter?: () => void
  onPointerLeave?: () => void
  className?: string
  label?: string
}) {
  const { t } = useTranslations('leads')
  const level = label ?? matchScoreToLevel(score, t)
  const bg = matchScoreToColor(score, variant)
  const fg = matchScoreToTextColor(score)
  const hexagonIcon = getHexagonIconName(score)

  const isInteractive = onPress || onPointerEnter || onPointerLeave

  const content = (
    <>
      <Heading3
        className={cn(
          'select-none font-semibold text-xs/relaxed',
          isInteractive && 'cursor-pointer underline underline-offset-2',
          variant === 'inverted' && 'drop-shadow-sm'
        )}
        style={{ color: variant === 'inverted' ? bg : fg }}>
        {level}
      </Heading3>
      <Icon
        name={hexagonIcon}
        size={16}
        color={variant === 'inverted' ? bg : fg}
        library="materialcommunityicons"
        className={cn(variant === 'inverted' && 'drop-shadow-sm')}
      />
    </>
  )

  const badgeClassName = cn(
    'select-none flex-row items-center gap-2 rounded-full px-3 py-1 transition-all duration-150',
    variant === 'inverted' && 'px-0',
    isInteractive && 'cursor-pointer hover:scale-[1.02] hover:opacity-90 active:scale-[0.98]',
    className
  )

  const badgeStyle = {
    backgroundColor: variant === 'inverted' ? 'transparent' : bg,
  }

  if (isInteractive) {
    return (
      <AnimatedPressable
        onPress={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onPress?.()
        }}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        className={badgeClassName}
        style={badgeStyle}>
        {content}
      </AnimatedPressable>
    )
  }

  return (
    <View className={badgeClassName} style={badgeStyle}>
      {content}
    </View>
  )
}
